import { noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    getOpenApiUpdateDashboardTodolistItemCompleteStatusUrl,
    OpenApiGetDashboardTodolistsMethod,
    type OpenApiGetDashboardTodolistsRequest,
    type OpenApiGetDashboardTodolistsResponseSuccess,
    OpenApiGetDashboardTodolistsUrl,
    OpenApiMoveDashboardTodolistStepMethod,
    type OpenApiMoveDashboardTodolistStepRequest,
    OpenApiMoveDashboardTodolistStepUrl,
    OpenApiUpdateDashboardTodolistItemCompleteStatusMethod,
    type OpenApiUpdateDashboardTodolistItemCompleteStatusRequest,
    type OpenApiUpdateDashboardTodolistItemCompleteStatusResponseSuccess,
} from '#shared/api/schema';

export type UpdateDashboardTodolistItemCompleteStatusArg = {
    id: string;
    body: OpenApiUpdateDashboardTodolistItemCompleteStatusRequest;
};

export type MoveDashboardTodolistStepArg = OpenApiMoveDashboardTodolistStepRequest;

const TodoTag = 'TodoTag';

const apiDashboardTodolist = createApi({
    name: 'entities/dashboard/api/todo',
    baseQuery: createBaseQuery(apiV1Request),
    tagTypes: [TodoTag],
    endpoints: (builder) => {
        return {
            get: builder.query<OpenApiGetDashboardTodolistsResponseSuccess, OpenApiGetDashboardTodolistsRequest>({
                query: () => {
                    return {
                        url: OpenApiGetDashboardTodolistsUrl,
                        method: OpenApiGetDashboardTodolistsMethod,
                    };
                },
                providesTags: [TodoTag],
            }),
            updateItemCompleteStatus: builder.mutation<
                OpenApiUpdateDashboardTodolistItemCompleteStatusResponseSuccess,
                UpdateDashboardTodolistItemCompleteStatusArg
            >({
                query: ({ id, body }) => {
                    return {
                        url: getOpenApiUpdateDashboardTodolistItemCompleteStatusUrl({ id }),
                        method: OpenApiUpdateDashboardTodolistItemCompleteStatusMethod,
                        body,
                    };
                },
                async onQueryStarted(_arg, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data: updatedItem } = queryFulfilledResult;

                    apiDashboardTodolist.endpoints.get.updateQueryData(undefined, (draft) => {
                        const indexOfCurrentTodolist = draft.todolists.findIndex((todolist) => todolist.is_current);

                        for (const todolist of draft.todolists) {
                            const index = todolist.items.findIndex((row) => row.id === updatedItem.id);

                            if (index !== -1) {
                                todolist.items[index] = updatedItem;

                                if (
                                    todolist.items.every((item) => item.completed_at) &&
                                    draft.todolists.length > indexOfCurrentTodolist + 1
                                ) {
                                    // Если все задачи выполнены, мы можем двигаться вперед
                                    draft.can_move_forward = true;
                                } else if (!updatedItem.completed_at) {
                                    // В случае, когда мы отменяем выполнение задачи, мы не можем двигаться вперед
                                    draft.can_move_forward = false;
                                }

                                break;
                            }
                        }
                    });
                },
            }),
            moveStep: builder.mutation<void, MoveDashboardTodolistStepArg>({
                query: (body) => {
                    return {
                        url: OpenApiMoveDashboardTodolistStepUrl,
                        method: OpenApiMoveDashboardTodolistStepMethod,
                        body,
                    };
                },
                invalidatesTags: [TodoTag],
            }),
        };
    },
});

export const useGetDashboardTodolists = apiDashboardTodolist.useGetQuery;
export const useUpdateDashboardTodolistItemCompleteStatusMutation =
    apiDashboardTodolist.useUpdateItemCompleteStatusMutation;
export const useMoveDashboardTodolistStepMutation = apiDashboardTodolist.useMoveStepMutation;
